import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { EquipmentRepairSearchRequest, EquipmentRepairViewModel, SerializedItemListOptionRequest, SerializedItemsReceiveViewModel } from '../models/serialized-items';
import { SerializedItemRemoveValidationResultViewModel } from '../models/serialized-items/serialized-item-remove-validation-result-view-model';
import { SerializedItemRemoveValidationRequest } from '../models/serialized-items/serialized-item-remove-validation-request';
import { SerializedItemRemoveRequest } from '../models/serialized-items/serialized-item-remove-request';
import { SerializedItemReturnToManufactureRequest } from '../models/serialized-items/serialized-item-return-to-manufacture-request';
import { SerializedItemAdjustRequest } from '../models/serialized-items/serialized-item-adjust-request';
import { DispensingCaseViewModel, InventoryDispensingViewModel } from '../models/serialized-items/serialized-item-inventory-search';
import { SerializedItemsReturnToManufactureRequest } from '../models/serialized-items/serialized-items-return-to-manufacture-request';

@Injectable({
  providedIn: 'root'
})
export class SerializedItemDataService {
  constructor(private readonly httpClient: ApiHttpClient) {
  }

  getSerializedItemOptionsAsync(query: string, clinicId: number, productStyleId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: SerializedItemListOptionRequest = { searchString: query, clinicId: clinicId, productStyleId: productStyleId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SerializedItems/Options', request, cancellationToken);
  }

  getSerializedItemOptionsForSalesOrderAsync(query: string, salesOrderId: number, productStyleId: number, patientId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: SerializedItemListOptionRequest = { searchString: query, salesOrderId: salesOrderId, productStyleId: productStyleId, patientId: patientId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SerializedItems/Options', request, cancellationToken);
  }

  getOptionsWithProductStyleNameAsync(query: string, salesOrderId: number, productStyleId: number, patientId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: SerializedItemListOptionRequest = { searchString: query, salesOrderId: salesOrderId, productStyleId: productStyleId, patientId: patientId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SerializedItems/OptionsWithProductStyleName', request, cancellationToken);
  }

  getReplacedOptionsWithProductStyleNameAsync(query: string, salesOrderId: number, productStyleId: number, patientId: number ,cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: SerializedItemListOptionRequest = { searchString: query, salesOrderId: salesOrderId, productStyleId: productStyleId, patientId: patientId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SerializedItems/ReplacedOptionsWithProductStyleName', request, cancellationToken);
  }

  sendToRepairAsync(serializedItems: EquipmentRepairSearchRequest, cancellation: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('SerializedItems/SendToRepair', serializedItems, cancellation);
  }

  getSendToRepairOptionFormAsync(clinicId: number, patientId: number, serializedItemsIds: number[], cancellation: Promise<any>): Promise<EquipmentRepairViewModel> {
    const request = { clinicId: clinicId, patientId: patientId, serializedItemsIds: serializedItemsIds };
    return this.httpClient.getJsonAsync('SerializedItems/GetSendToRepairItems', request, cancellation);
  }

  returnSerializedItemAsync(selectedLineIds: number[], cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItems/ReturnItems', selectedLineIds, cancellationToken, true);
  }

  checkProductStyleSerialExistsAsync(productStyleId: number, serial: string, id: number, receivingAgainstCancelledPO: boolean, cancellationToken: Promise<any>): Promise<boolean> {
    const request = { productStyleId: productStyleId, serial: serial, id: id, receivingAgainstCancelledPO: receivingAgainstCancelledPO };
    return this.httpClient.getJsonAsync<boolean>('SerializedItems/CheckProductStyleSerialExists', request, cancellationToken, false);
  }

  repairSerializedItemAsync(selectedLineIds: number[], cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItems/Repair', selectedLineIds, cancellationToken);
  }

  validateBeforeRemoveSerializedItemAsync(id: number, cancellationToken: Promise<any>): Promise<SerializedItemRemoveValidationResultViewModel> {
    const request = { id: id } as SerializedItemRemoveValidationRequest;
    return this.httpClient.getJsonAsync('SerializedItems/ValidateBeforeRemove', request, cancellationToken);
  }

  removeSerializedItemAsync(id: number, cancellationToken: Promise<any>): Promise<any> {
    const request = { id: id } as SerializedItemRemoveRequest;
    return this.httpClient.postJsonAsync('SerializedItems/Remove', request, cancellationToken);
  }

  removeRecievedSerializedItemAsync(request: SerializedItemsReceiveViewModel, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItemsReceive/Remove', request, cancellationToken);
  }

  updateRecievedSerializedItemAsync(request: SerializedItemsReceiveViewModel, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItemsReceive/Update', request, cancellationToken);
  }

  adjustSerializedItemAsync(request: SerializedItemAdjustRequest, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItems/AdjustSerializedItem', request, cancellationToken);
  }

  returnToManufactureSerializedItemAsync(request: SerializedItemReturnToManufactureRequest, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItems/ReturnToManufactureSerializedItem', request, cancellationToken);
  }

  returnToManufactureSerializedItemsAsync(request: SerializedItemsReturnToManufactureRequest, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('SerializedItems/ReturnToManufactureSerializedItems', request, cancellationToken);
  }

  getColorOnSerialChanged(serializedItemId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request = { serializedItemId: serializedItemId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('SerializedItems/GetColorOnSerialChanged', request, cancellationToken);
  }

  orderIntegrationRequired(clinicId: number, serializedItemsIds: number[], cancellation: Promise<any>): Promise<boolean> {
    const request = { clinicId: clinicId, serializedItemsIds: serializedItemsIds };
    return this.httpClient.getJsonAsync('SerializedItems/OrderIntegrationRequired', request, cancellation);

  }

  DispensingInventorySearch(data: InventoryDispensingViewModel, cancellationToken: Promise<any>): Promise<DispensingCaseViewModel> {
    const request = data;
    return this.httpClient.getJsonAsync('SerializedItems/DispensingInventorySearch', request, cancellationToken);
  }
}
